import { Box } from '@mui/material';
import { ReactElement, SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toTitleCase } from '@shared/utils/utilsOld';
import UtmsChip from '@shared/components/ui/UtmsChip';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';

interface OverflowChipListForTableProps<T> {
    array: Array<T>;
    getLabel: (item: T) => string;
    getId: (item: T) => string | number;
    getVariant?: (item: T) => 'filled' | 'outlined';
    lineCount?: number;
    chipMaxWidth?: number;
}
const OverflowChipListForTable = <T,>({
    array,
    getId,
    getLabel,
    lineCount = 4,
    chipMaxWidth = 115,
    getVariant,
}: OverflowChipListForTableProps<T>): ReactElement => {
    const [showAllItems, setShowAllItems] = useState(false);
    const { t } = useTranslation();

    const { shownArray, hiddenCount } = useMemo(() => {
        return {
            shownArray: array.slice(0, lineCount),
            hiddenCount: array.length - lineCount,
        };
    }, [array, lineCount]);

    const handleShowAllItems = (e: SyntheticEvent) => {
        e.stopPropagation();
        setShowAllItems((prev) => !prev);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: '100%' }}>
            {(showAllItems ? array : shownArray).map((item) => {
                const id = getId(item);
                const label = getLabel(item);
                const chipVariant = getVariant && getVariant(item);

                // TODO getVariant for schemes: item?.auto !== undefined ? (item.auto ? 'filled' : 'outlined') : 'filled'

                return (
                    <UtmsChip
                        key={id}
                        label={
                            <OverflowTooltip
                                title={label}
                                sx={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    lineHeight: '143%',
                                    letterSpacing: '0.01063rem',
                                }}
                            >
                                {label}
                            </OverflowTooltip>
                        }
                        sx={{
                            width: 'fit-content',
                        }}
                        variant={chipVariant || 'filled'}
                    />
                );
            })}
            {hiddenCount > 0 && (
                <UtmsChip
                    variant="outlined"
                    label={showAllItems ? toTitleCase(t('hide') + '...') : '+' + hiddenCount}
                    sx={{
                        maxWidth: chipMaxWidth,
                        width: 'fit-content',
                    }}
                    onClick={handleShowAllItems}
                />
            )}
        </Box>
    );
};

export default OverflowChipListForTable;
